import { BillingUiStateKind, Team } from '@this+that/models';
import { getBillingUiState } from '@this+that/utils';

import { getDaysUntil } from './billingFormatters';

export enum TrialBannerStage {
  CALM = 'CALM',
  WARNING = 'WARNING',
  URGENT = 'URGENT',
  EXPIRED = 'EXPIRED',
}

export interface TrialBannerState {
  stage: TrialBannerStage;
  daysLeft: number;
}

// Last day of the trial.
const URGENT_MAX_DAYS = 1;
// The week before that.
const WARNING_MAX_DAYS = 7;

type TrialBannerTeam = Pick<
  Team,
  'subscriptionStatus' | 'cancelAtPeriodEnd' | 'trialEndsAt'
>;

function resolveAppTrialStage(daysLeft: number): TrialBannerStage {
  if (daysLeft <= URGENT_MAX_DAYS) {
    return TrialBannerStage.URGENT;
  }
  if (daysLeft <= WARNING_MAX_DAYS) {
    return TrialBannerStage.WARNING;
  }
  return TrialBannerStage.CALM;
}

// null means "no banner": a Stripe subscription already exists (a card is on
// file), or the team is STARTER and never had an app-level trial.
export function getTrialBannerStage(
  team: TrialBannerTeam,
  now: number = Date.now(),
): TrialBannerState | null {
  const uiState = getBillingUiState(team, now);

  if (uiState.kind === BillingUiStateKind.APP_TRIAL_EXPIRED) {
    return {
      stage: TrialBannerStage.EXPIRED,
      daysLeft: getDaysUntil(team.trialEndsAt ?? now, now),
    };
  }

  if (uiState.kind !== BillingUiStateKind.APP_TRIAL) {
    return null;
  }

  const daysLeft = getDaysUntil(team.trialEndsAt ?? now, now);
  return { stage: resolveAppTrialStage(daysLeft), daysLeft };
}
